import { useCallback, useMemo } from "react";
import type { OperationsAttentionKey, OperationsItemsFilter, OperationsLensKey, OperationsParams, OperationsPenKey, OperationsStageKey } from "@/api/endpoints";
import { BrandedLoader } from "@/components/BrandedLoader";
import { TableSkeleton, TilesSkeleton } from "@/components/Skeleton";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import { clearedDrill, hasDrill, type OperationsState } from "./operationsSchema";
import { countLabel, sortSummary, type DrawerView } from "./drawerView";
import { formatWaiting, isSnapshotWarming, WARMING_SLOW_AFTER_MS, WARMING_STEP_LABEL, warmingReason } from "./operationsWarming";
import { useWarmingClock } from "./useWarmingClock";
import { useOperationsUrlState } from "./useOperationsUrlState";
import { useOperationsIntakeItemsQuery, useOperationsItemsQuery, useOperationsJourneyItemsQuery, useOperationsSummaryQuery } from "./useOperationsQuery";
import { ATTENTION_LABEL, INTAKE_ROW_LABEL, lensSuffix, requestStatusLabel, STAGE_LABEL, workTypeLabel } from "./operationsLabels";
import { hasActiveFilters, SlicerBar } from "./components/SlicerBar";
import { StampLine } from "./components/StampLine";
import { LensControl } from "./components/LensControl";
import { PenControl } from "./components/PenControl";
import { FlowTable } from "./components/FlowTable";
import { WipByStage } from "./components/WipByStage";
import { AttentionTiles } from "./components/AttentionTiles";
import { StartDateTiles } from "./components/StartDateTiles";
import { AgeHistogram } from "./components/AgeHistogram";
import { ArrivalsLine } from "./components/ArrivalsLine";
import { OwnerTable } from "./components/OwnerTable";
import { ChecksStrip } from "./components/ChecksStrip";
import { ItemsDrawer } from "./components/ItemsDrawer";
import { IntakeDrawer } from "./components/IntakeDrawer";
import { JourneyDrawer } from "./components/JourneyDrawer";
import { AssistantPanel } from "@/features/assistant/AssistantPanel";
import { useFeatureFlags } from "@/config/useFeatureFlags";
import { assistantDrill, scopeLabel, toAssistantContext } from "./operationsAssistantContext";

function toParams(state: OperationsState): OperationsParams {
  return {
    period: state.period,
    lens: state.lens,
    pen: state.pen,
    onboardingsOnly: state.onboardingsOnly,
    redeployment: state.redeployment,
    requestType: state.requestType,
    client: state.client,
    lob: state.lob,
    eng: state.eng,
    assigned: state.assigned,
    team: state.team,
    division: state.division,
    manager: state.manager,
    country: state.country,
  };
}

function toItemsFilter(state: OperationsState): OperationsItemsFilter {
  return {
    attention: state.dAttention,
    stage: state.dStage,
    status: state.dStatus,
    owner: state.dOwner,
    ownerTeam: state.dTeam,
    ownerDivision: state.dDivision,
    ownerManager: state.dManager,
    workType: state.dWorkType,
    flow: state.dFlow,
    day: state.dDay,
    dayKind: state.dDayKind,
    stuckBy: state.dStuckBy,
    attentionIn: state.dfAttention,
    pendingWith: state.dfPending,
    workTypeIn: state.dfWorkType,
    startRiskIn: state.dfStartRisk,
    ownerIn: state.dfOwner,
    teamIn: state.dfTeam,
    divisionIn: state.dfDivision,
    managerIn: state.dfManager,
    engagementIn: state.dfEngagement,
    holderIn: state.dfHolder,
    q: state.dfQ,
    sort: state.dSort,
    dir: state.dDir,
  };
}

/** The intake drawer's title: the open intakes, with the lenses. */
function intakeDrillTitle(lens: OperationsLensKey | undefined, pen: OperationsPenKey | undefined): string {
  return `Open items · ${INTAKE_ROW_LABEL}${lensSuffix(lens, pen)}`;
}

/** The age bar's title: the bar in business days; the lens cuts the bar like every open drill. */
function ageDrillTitle(bin: number, lens: OperationsLensKey | undefined, pen: OperationsPenKey | undefined): string {
  return `Open items aged ${bin > 30 ? "30+" : bin} business days${lensSuffix(lens, pen)}`;
}

/** The start-date tiles' title: the cohort and, when one work type was drilled, which. Not lensed. */
function startRiskDrillTitle(risk: "atRisk" | "breached", workType: string | undefined): string {
  const what = workType === "Extension" ? "extensions" : workType === "Onboarding" ? "onboarding journeys" : "journeys";
  return risk === "atRisk" ? `At risk · ${what} starting within the window, unapproved` : `Breached · ${what} unapproved on or after their start date`;
}

/** The journey drawer's title: the work type and the cohort; the period for started / ended, the lens for open only. */
function journeyDrillTitle(flow: "open" | "started" | "ended", workType: string | undefined, periodLabel: string, lens: OperationsLensKey | undefined, pen: OperationsPenKey | undefined): string {
  const noun = `${workType ? workTypeLabel(workType) : "All"} journeys`;
  const consultant = !workType || workType === "Onboarding" || workType === "Redeployment";
  if (flow === "started") return `${noun} · started ${periodLabel}`;
  if (flow === "ended") return `${noun} · ${consultant ? "WO approved" : "ended"} ${periodLabel}`;
  return `Open ${noun.toLowerCase()}${lensSuffix(lens, pen)}`;
}

/** Lensed buckets are every open-population drill: not started / ended, not an arrivals day. */
function drillIsLensed(state: OperationsState): boolean {
  return !!(state.lens || state.pen) && (state.dFlow === undefined || state.dFlow === "open") && state.dDay === undefined;
}

/** Human title for the open drawer, from whichever drill keys are set. */
function drillTitle(state: OperationsState): string {
  // An attention drill names its state already; the pen still applies to it.
  const suffix = drillIsLensed(state) ? lensSuffix(state.dAttention ? undefined : state.lens, state.pen) : "";
  return baseDrillTitle(state) + suffix;
}

function baseDrillTitle(state: OperationsState): string {
  if (state.dOwner) return state.dFlow === "started" ? `${state.dOwner} · created this period` : state.dFlow === "ended" ? `${state.dOwner} · completed this period` : state.dOwner;
  if (state.dTeam) return `Team ${state.dTeam} · open items`;
  if (state.dManager) return `${state.dManager}'s teams · open items`;
  if (state.dDivision) return `${state.dDivision} · open items`;
  if (state.dStage && state.dStatus) return `${STAGE_LABEL[state.dStage]} · ${requestStatusLabel(state.dStatus)}`;
  if (state.dStage) return `Open items · ${STAGE_LABEL[state.dStage]}`;
  if (state.dAttention) {
    const by = state.dStuckBy === "workOrder" ? " by work order" : state.dStuckBy === "pendingTask" ? " by pending task" : "";
    return `${ATTENTION_LABEL[state.dAttention]}${by} · open items`;
  }
  if (state.dFlow) {
    const noun = state.dWorkType ? workTypeLabel(state.dWorkType) : "All types";
    return state.dFlow === "started" ? `${noun} · started in period` : state.dFlow === "ended" ? `${noun} · completed in period` : `${noun} · open now`;
  }
  if (state.dDay) return `${state.dDayKind === "departure" ? "Departures" : "Arrivals"} on ${state.dDay}`;
  return "Items";
}

// Clear filters resets the two lenses with the slicers (2026-10-28): the link shows while either lens is on.
const EMPTY_FILTERS: Partial<OperationsState> = {
  lens: undefined, pen: undefined,
  onboardingsOnly: false, redeployment: false,
  requestType: [], client: [], lob: [], eng: [], assigned: [], team: [], division: [], manager: [], country: [],
};

/**
 * Operations dashboard: one screen for the daily scrum. Eight panels, all filters over one summary
 * response; every number drills to its rows in a drawer. Behind Features:Operations (route + tab +
 * API are all gated).
 */
export function OperationsScreen() {
  const [state, setState] = useOperationsUrlState();
  const params = useMemo(() => toParams(state), [state]);
  const summary = useOperationsSummaryQuery(params);
  const data = summary.data;

  const intakeDrillOpen = state.dIntakeFlow !== undefined;
  // The journey drawer serves the Flow card's journey rows and every bar of the age card.
  const journeyDrillOpen = state.dJourneyFlow !== undefined || state.dAgeBin !== undefined;
  const drillOpen = hasDrill(state) && !intakeDrillOpen && !journeyDrillOpen;
  const itemsFilter = useMemo(() => toItemsFilter(state), [state]);
  const items = useOperationsItemsQuery(params, itemsFilter, drillOpen);
  const intakeItems = useOperationsIntakeItemsQuery(params, {}, intakeDrillOpen);
  // The owner / team / division desk views (2026-10-27) also list the intakes the person holds; the drawer's search applies.
  // A period-movement drill (an owner's New assigned / Departures, 2026-10-28) lists requests only: no held intakes.
  const deskDrillOpen = drillOpen && state.dFlow === undefined && (state.dOwner !== undefined || state.dTeam !== undefined || state.dDivision !== undefined || state.dManager !== undefined);
  const deskIntakes = useOperationsIntakeItemsQuery(params, { owner: state.dOwner, ownerTeam: state.dTeam, ownerDivision: state.dDivision, ownerManager: state.dManager, q: state.dfQ }, deskDrillOpen);
  const journeyWorkType = state.dWorkType;
  const journeyItems = useOperationsJourneyItemsQuery(params, { flow: state.dJourneyFlow ?? "open", workType: journeyWorkType, ageBin: state.dAgeBin, startRisk: state.dStartRisk }, journeyDrillOpen);

  // Other filters than the data on screen (placeholderData keeps the previous key's answer): the old numbers must
  // read as loading at once, or a glance takes them for the new ones. A background refresh of the same key keeps the
  // view and shows the overlay only when it takes longer than the delay.
  const staleData = !!data && summary.isPlaceholderData && summary.isFetching;
  const showLoader = useDelayedFlag(summary.isFetching && !!data && !summary.isPlaceholderData);
  const itemsUpdating = !!items.data && items.isPlaceholderData && items.isFetching;
  const intakeUpdating = !!intakeItems.data && intakeItems.isPlaceholderData && intakeItems.isFetching;
  const deskIntakesUpdating = !!deskIntakes.data && deskIntakes.isPlaceholderData && deskIntakes.isFetching;
  const journeysUpdating = !!journeyItems.data && journeyItems.isPlaceholderData && journeyItems.isFetching;
  // 503 from the API while its background warmer builds the wide caches: keep waiting, say so.
  const warming = !data && isSnapshotWarming(summary.failureReason);
  const itemsWarming = !items.data && isSnapshotWarming(items.failureReason);
  const warmingStep = warming ? warmingReason(summary.failureReason) : null;
  const warmingMs = useWarmingClock(warming && summary.isPending);
  const periodLabel = state.period === "today" ? "today" : "week to date";
  const stuckAfterBd = data?.stamp.stuckAfterBd ?? 1;
  const blockedAfterBd = data?.stamp.blockedAfterBd ?? 3;
  const atRiskAfterBd = data?.stamp.atRiskAfterBd ?? 2;
  const breachedAfterBd = data?.stamp.breachedAfterBd ?? 0;

  const assistantContext = useMemo(() => toAssistantContext(state), [state]);
  const assistantExtras = useMemo(
    () => ({ scope: scopeLabel(state), drill: assistantDrill(state), dataAsOfUtc: data?.stamp.loadedAtUtc }),
    [state, data?.stamp.loadedAtUtc],
  );
  const flags = useFeatureFlags();
  const drill = useCallback((next: Partial<OperationsState>) => setState({ ...clearedDrill(), ...next }), [setState]);
  const closeDrill = useCallback(() => setState(clearedDrill()), [setState]);
  const drawerView = useMemo<DrawerView>(
    () => ({ sort: state.dSort, dir: state.dDir, attention: state.dfAttention, pendingWith: state.dfPending, workType: state.dfWorkType, startRisk: state.dfStartRisk, owner: state.dfOwner, team: state.dfTeam, division: state.dfDivision, manager: state.dfManager, engagement: state.dfEngagement, holder: state.dfHolder, q: state.dfQ ?? "" }),
    [state.dSort, state.dDir, state.dfAttention, state.dfPending, state.dfWorkType, state.dfStartRisk, state.dfOwner, state.dfTeam, state.dfDivision, state.dfManager, state.dfEngagement, state.dfHolder, state.dfQ],
  );
  const changeDrawerView = useCallback(
    (next: Partial<DrawerView>) =>
      setState({
        ...("sort" in next ? { dSort: next.sort } : {}),
        ...("dir" in next ? { dDir: next.dir } : {}),
        ...(next.attention ? { dfAttention: next.attention } : {}),
        ...(next.pendingWith ? { dfPending: next.pendingWith } : {}),
        ...(next.workType ? { dfWorkType: next.workType } : {}),
        ...(next.startRisk ? { dfStartRisk: next.startRisk } : {}),
        ...(next.owner ? { dfOwner: next.owner } : {}),
        ...(next.team ? { dfTeam: next.team } : {}),
        ...(next.division ? { dfDivision: next.division } : {}),
        ...(next.manager ? { dfManager: next.manager } : {}),
        ...(next.engagement ? { dfEngagement: next.engagement } : {}),
        ...(next.holder ? { dfHolder: next.holder } : {}),
        ...("q" in next ? { dfQ: next.q?.trim() ? next.q : undefined } : {}),
      }),
    [setState],
  );
  const clearFilters = () => setState({ ...EMPTY_FILTERS, ...clearedDrill() });

  // "Needs attention" = everything open that is not Clear (the states are exclusive).
  const attentionRow = (state: OperationsAttentionKey) => data?.attention.states.find((s) => s.state === state);
  const attentionCount = data ? data.attention.openNow - (attentionRow("clear")?.count ?? 0) : 0;
  const stuckByWorkOrder = (attentionRow("stuck")?.fromWorkOrder ?? 0) + (attentionRow("blocked")?.fromWorkOrder ?? 0) + (attentionRow("stuckAndBlocked")?.fromWorkOrder ?? 0);
  const lens = state.lens;
  const setLens = useCallback((next: OperationsLensKey | undefined) => setState({ lens: next, ...clearedDrill() }), [setState]);
  const pen = state.pen;
  const setPen = useCallback((next: OperationsPenKey | undefined) => setState({ pen: next, ...clearedDrill() }), [setState]);
  const anyLens = !!(lens || pen);

  const isEmpty = !!data && data.attention.openNow === 0 && data.flow.total.started === 0 && data.flow.total.ended === 0;

  return (
    <>
      <div className="page-head ops-page-head">
        <div>
          <h1>Operations</h1>
          <div className="subtitle">
            Work in process right now: what is open, where it sits, what is waiting on whom, and who holds it. Observation only; no targets.
          </div>
        </div>
        {/* View controls (how the board is shown), apart from the filters (which requests are in it). Business days only. */}
        <div className="ops-view-ctl">
          <LensControl value={state.lens} onChange={setLens} />
          <PenControl value={state.pen} onChange={setPen} />
        </div>
      </div>

      <div className="screen-body">
        {staleData ? <BrandedLoader label="Updating for the new filters…" /> : showLoader && <BrandedLoader />}

        <SlicerBar state={state} options={data?.filterOptions} onChange={setState} />

        {data && <StampLine state={state} stamp={data.stamp} hasFilters={hasActiveFilters(state)} updating={staleData} onClear={clearFilters} />}

        {warming && summary.isPending && (
          <div className="card" role="status" aria-live="polite">
            <div className="empty">
              <span className="material-symbols-rounded ops-warming-icon" aria-hidden="true">hourglass_top</span>
              <h4>Building the first snapshot</h4>
              <p>
                After an API start the server loads 200 days of requests and task actions in the background.
                The page refreshes itself as soon as they are ready.
              </p>
              <p className="ops-warming-step">
                {warmingStep ? WARMING_STEP_LABEL[warmingStep] : "Waiting for the server"} · waiting {formatWaiting(warmingMs)}
              </p>
              {warmingMs >= WARMING_SLOW_AFTER_MS && (
                <p className="ops-warming-slow">
                  This is taking longer than usual. In the API console, "Cache warm" lines show each step finishing;
                  a "Cache warmer cycle failed" line names the step that failed, and the server retries it after 30 seconds.
                </p>
              )}
            </div>
          </div>
        )}

        {summary.isPending && !data && !warming && (
          <div className="ops-grid" aria-busy="true">
            <div className="ops-panel ops-span-8"><TableSkeleton cols={4} rows={5} /></div>
            <div className="ops-panel ops-span-4"><TableSkeleton cols={3} rows={5} /></div>
            <div className="ops-span-12"><TilesSkeleton count={4} /></div>
            <div className="ops-panel ops-span-8" style={{ minHeight: 220 }} />
            <div className="ops-panel ops-span-4" style={{ minHeight: 220 }} />
            <div className="ops-span-12 card"><TableSkeleton cols={11} rows={6} /></div>
          </div>
        )}

        {summary.isError && !data && (
          <div className="card">
            <div className="empty">
              <span className="material-symbols-rounded" style={{ fontSize: 40, color: "var(--gw-error-500)" }} aria-hidden="true">error</span>
              <h4>Could not load the operations snapshot</h4>
              <p>{(summary.error as Error)?.message ?? "The API did not answer."} Nothing on this screen is cached in the browser, so there is no stale view to show.</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => summary.refetch()}>
                <span className="material-symbols-rounded" aria-hidden="true">refresh</span>
                Retry
              </button>
            </div>
          </div>
        )}

        {data && isEmpty && (
          <div className="card">
            <div className="empty">
              <span className="material-symbols-rounded" style={{ fontSize: 40, color: "var(--gw-primary-100)" }} aria-hidden="true">inbox</span>
              <h4>No open requests match these filters</h4>
              <p>Nothing is in flight for this combination. Widen a filter, or check the Requests screen for completed work.</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear filters</button>
            </div>
          </div>
        )}

        {data && !isEmpty && (
          <div className={`ops-grid${staleData ? " is-stale" : ""}`} aria-busy={staleData || undefined}>
            <FlowTable
              flow={data.flow}
              periodLabel={periodLabel}
              attentionCount={attentionCount}
              lens={lens}
              pen={pen}
              lensedOpenNow={data.lensedOpenNow}
              stuckByWorkOrder={stuckByWorkOrder}
              onDrill={(workType, cohort) => drill({ dWorkType: workType, dJourneyFlow: cohort })}
            />
            <WipByStage
              rows={data.wipByStage}
              lens={lens}
              pen={pen}
              lensedOpenNow={data.lensedOpenNow}
              openNow={data.attention.openNow}
              onDrill={(stage: OperationsStageKey) => drill({ dStage: stage })}
              intake={data.intake}
              onDrillIntake={() => drill({ dIntakeFlow: "open" })}
            />
            <AttentionTiles
              openNow={data.attention.openNow}
              states={data.attention.states}
              stuckAfterBd={stuckAfterBd}
              blockedAfterBd={blockedAfterBd}
              active={state.dAttention}
              lens={lens}
              pen={pen}
              onDrill={(key: OperationsAttentionKey, stuckBy?: "workOrder") => drill({ dAttention: key, dStuckBy: stuckBy })}
              onLens={setLens}
            />
            <StartDateTiles
              rows={data.startRisk}
              openNow={data.flow.total.openNowAll}
              atRiskAfterBd={atRiskAfterBd}
              breachedAfterBd={breachedAfterBd}
              active={state.dStartRisk === "atRisk" || state.dStartRisk === "breached" ? state.dStartRisk : undefined}
              onDrill={(risk, workType) => drill({ dJourneyFlow: "open", dStartRisk: risk, dWorkType: workType })}
            />
            <AgeHistogram
              age={data.age}
              colorBy={state.ageColor}
              onColorBy={(ageColor) => setState({ ageColor })}
              activeBin={state.dAgeBin}
              lens={lens}
              pen={pen}
              openNow={data.flow.total.openNowAll}
              onDrill={(bin) => drill({ dAgeBin: bin })}
            />
            <ArrivalsLine days={data.arrivals} unlensed={anyLens} onDrill={(day, kind) => drill({ dDay: day, dDayKind: kind })} />
            <OwnerTable
              owners={data.owners}
              total={data.ownerTotal}
              periodLabel={periodLabel}
              blockedAfterBd={blockedAfterBd}
              activeOwner={state.dOwner}
              activeTeam={state.dTeam}
              activeDivision={state.dDivision}
              activeManager={state.dManager}
              lens={lens}
              pen={pen}
              weights={data.workTypeWeights}
              defaultWeight={data.defaultWorkTypeWeight}
              directory={data.directory}
              onDrill={(target, preset) => {
                // A group row's At risk / Breached count is journeys: the journey drawer, cut to the group locally.
                if (preset?.startRisk && !("dOwner" in target)) drill({ dJourneyFlow: "open", dStartRisk: preset.startRisk, ...target });
                // The table's Stuck / Blocked columns include the items that are both: tick both chips so the drawer's count matches.
                else drill({ ...target, dfAttention: preset?.attention ? [preset.attention, "stuckAndBlocked"] : [] });
              }}
            />
            {data.showConsistencyChecks && <ChecksStrip checks={data.checks} lens={lens} pen={pen} lensedOpenNow={data.lensedOpenNow} />}
          </div>
        )}
      </div>

      {intakeDrillOpen && (
        <IntakeDrawer
          title={intakeDrillTitle(lens, pen)}
          data={intakeItems.data}
          loading={intakeItems.isPending}
          updating={intakeUpdating}
          error={intakeItems.isError ? ((intakeItems.error as Error)?.message ?? "Unknown error") : null}
          onClose={closeDrill}
          onRetry={() => intakeItems.refetch()}
        />
      )}

      {journeyDrillOpen && (
        <JourneyDrawer
          title={(state.dAgeBin !== undefined ? ageDrillTitle(state.dAgeBin, lens, pen)
            : state.dStartRisk === "atRisk" || state.dStartRisk === "breached" ? startRiskDrillTitle(state.dStartRisk, journeyWorkType)
            : journeyDrillTitle(state.dJourneyFlow ?? "open", journeyWorkType, periodLabel, lens, pen))
            + (state.dTeam ? ` · Team ${state.dTeam}` : state.dManager ? ` · ${state.dManager}'s teams` : state.dDivision ? ` · ${state.dDivision}` : "")}
          cohort={state.dStartRisk === "atRisk" || state.dStartRisk === "breached" ? state.dStartRisk : undefined}
          presetTeam={state.dTeam}
          presetDivision={state.dDivision}
          presetManager={state.dManager}
          data={journeyItems.data}
          loading={journeyItems.isPending}
          updating={journeysUpdating}
          error={journeyItems.isError ? ((journeyItems.error as Error)?.message ?? "Unknown error") : null}
          onClose={closeDrill}
          onRetry={() => journeyItems.refetch()}
        />
      )}

      {drillOpen && (
        <ItemsDrawer
          title={drillTitle(state)}
          meta={items.data ? (deskDrillOpen ? sortSummary(drawerView) : `${countLabel(items.data.totalCount, items.data.bucketCount)} · ${sortSummary(drawerView)}`) : "Loading…"}
          intakes={deskDrillOpen ? { data: deskIntakes.data, loading: deskIntakes.isPending, updating: deskIntakesUpdating, error: deskIntakes.isError ? ((deskIntakes.error as Error)?.message ?? "Unknown error") : null, onRetry: () => deskIntakes.refetch() } : undefined}
          items={items.data?.items ?? []}
          totalCount={items.data?.totalCount ?? 0}
          bucketCount={items.data?.bucketCount ?? items.data?.totalCount ?? 0}
          cap={items.data?.cap ?? 250}
          loading={items.isPending}
          updating={itemsUpdating}
          warming={itemsWarming}
          error={items.isError ? ((items.error as Error)?.message ?? "Unknown error") : null}
          blockedAfterBd={blockedAfterBd}
          hideOwner={!!state.dOwner}
          cohort={state.dFlow === "started" || state.dFlow === "ended" ? state.dFlow : undefined}
          hideTeam={!!state.dTeam}
          hideDivision={!!state.dTeam || !!state.dDivision || !!state.dManager}
          hideManager={!!state.dTeam || !!state.dManager}
          view={drawerView}
          facets={items.data?.facets}
          onViewChange={changeDrawerView}
          onClose={closeDrill}
          onRetry={() => items.refetch()}
        />
      )}

      {/* Behind Features:OperationsAssistant so the chat can be switched per environment. No filter
          rail on this screen, so the collapsed control floats like on Request Detail. */}
      {flags.operationsAssistant && (
        <AssistantPanel screen="operations" activeFilters={assistantContext} context={assistantExtras} floatingCollapsed />
      )}
    </>
  );
}
